import React from 'react';
import { Avatar, Box, Tooltip, Typography } from '@mui/material';
import type { ParticipantPreview } from '../types/Activity';
import type { SxProps, Theme } from '@mui/material/styles';

interface ParticipantsPreviewStackProps {
  participants?: ParticipantPreview[];
  totalCount?: number;
  maxVisible?: number;
  avatarSize?: number;
  dense?: boolean;
  sx?: SxProps<Theme>;
}

const ParticipantsPreviewStack: React.FC<ParticipantsPreviewStackProps> = ({
  participants = [],
  totalCount,
  maxVisible = 3,
  avatarSize = 32,
  dense = false,
  sx,
}) => {
  if (!participants || participants.length === 0) {
    return null;
  }

  const visibleParticipants = participants.slice(0, Math.max(maxVisible, 1));
  if (visibleParticipants.length === 0) {
    return null;
  }

  const totalParticipants = typeof totalCount === 'number' ? totalCount : participants.length;
  const extraCount = Math.max(totalParticipants - visibleParticipants.length, 0);
  const overlapOffset = Math.min(avatarSize * 0.4, avatarSize - 8);

  return (
    <Box
      aria-label="Participants preview"
      sx={{
        display: 'flex',
        alignItems: 'center',
        mt: dense ? 0.5 : 1,
        gap: 0.75,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {visibleParticipants.map((participant, index) => (
          <Tooltip key={`${participant.id ?? 'participant'}-${index}`} title={participant.name || 'TreffPunkts member'} arrow>
            <Avatar
              src={participant.profile_picture_url || undefined}
              alt={participant.name || 'TreffPunkts member'}
              sx={{
                width: avatarSize,
                height: avatarSize,
                fontSize: avatarSize * 0.45,
                ml: index === 0 ? 0 : `-${overlapOffset}px`,
                border: '2px solid #fff',
                bgcolor: '#6A4C93',
                color: '#fff',
                boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
              }}
            >
              {(participant.name?.charAt(0) || '?').toUpperCase()}
            </Avatar>
          </Tooltip>
        ))}
      </Box>
      {extraCount > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          +{extraCount}
        </Typography>
      )}
    </Box>
  );
};

export default ParticipantsPreviewStack;

